
import React, { useEffect, useRef } from 'react';
import { Message } from '@/types/chat';
import { User } from 'lucide-react';
import ChatMessage from './ChatMessage';
import { renderSafeHTML, formatMessage } from '@/utils/htmlUtils';

interface ChatMessagesProps {
  messages: Message[];
  isTyping: boolean;
  renderFilePreview: (file: File) => React.ReactNode;
  isExpanded: boolean;
  hasUserSentMessage: boolean;
  onPromptClick?: (prompt: string) => void;
}

const ChatMessages = ({
  messages,
  isTyping,
  renderFilePreview,
  isExpanded,
  hasUserSentMessage,
  onPromptClick
}: ChatMessagesProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const samplePrompts = ["How can you help me?"];

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Scroll to bottom when messages change or typing state changes
  useEffect(() => {
    // Small timeout to ensure DOM has updated
    const scrollTimer = setTimeout(() => {
      scrollToBottom();
    }, 500); // Increased timeout to ensure render completes
    
    return () => clearTimeout(scrollTimer);
  }, [messages, isTyping]);

  // Function to handle prompt click
  const handlePromptClick = (prompt: string) => {
    if (onPromptClick) {
      onPromptClick(prompt);
    }
  };

  return (
    <div className={`flex-grow overflow-y-auto p-4 space-y-4 ${isExpanded ? '' : 'h-80'}`}>
      {messages.map((message, index) => (
        <ChatMessage 
          key={index} 
          message={message} 
          renderFilePreview={renderFilePreview} 
        />
      ))}
      
      {/* Typing indicator */}
      {isTyping && (
        <div className="flex">
          <div className="h-8 w-8 rounded-full bg-goldcore/20 flex-shrink-0 flex items-center justify-center mr-2">
            <img src="/lovable-uploads/0aa296c2-39b8-4316-97b8-2a5754cf1180.png" alt="KnightAI" className="h-5 w-5" />
          </div>
          <div className="bg-goldcore/10 rounded-lg rounded-tl-none p-4">
            <div className="flex space-x-1">
              <div className="h-2 w-2 rounded-full bg-softwhite/70 animate-bounce"></div>
              <div className="h-2 w-2 rounded-full bg-softwhite/70 animate-bounce" style={{
                animationDelay: '0.2s'
              }}></div>
              <div className="h-2 w-2 rounded-full bg-softwhite/70 animate-bounce" style={{
                animationDelay: '0.4s'
              }}></div>
            </div>
          </div>
        </div>
      )}
      
      {/* Empty state when no messages and not typing */}
      {messages.length === 0 && !isTyping && (
        <div className="flex flex-col justify-center items-center h-full space-y-6">
          <p className="text-softwhite/50 text-center">
            Tell KnightAI about your business to get a custom Notion plan.
          </p>
          
          {!hasUserSentMessage && (
            <div className="space-y-3 w-full max-w-lg">
              <div className="flex flex-col gap-2">
                {samplePrompts.map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => handlePromptClick(prompt)}
                    className="px-4 py-3 bg-deepshadow/30 hover:bg-deepshadow/50 rounded-lg text-sm transition-colors text-softwhite text-center"
                  >
                    "{prompt}"
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Invisible div for scrolling reference */}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatMessages;
